import React, { useState } from 'react';
import { Calculator as CalcIcon, DollarSign, TrendingUp, Clock, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const Calculator = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [fraudType, setFraudType] = useState('');
  const [timeElapsed, setTimeElapsed] = useState('');
  const [hasDocuments, setHasDocuments] = useState('');
  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const fraudTypes = [
    { id: 'forex', label: t.forexBroker, successRate: 92, timeMultiplier: 1 },
    { id: 'crypto', label: t.cryptoScam, successRate: 78, timeMultiplier: 1.3 },
    { id: 'pyramid', label: t.pyramid, successRate: 65, timeMultiplier: 1.5 },
    { id: 'investment', label: t.investmentFund, successRate: 85, timeMultiplier: 1.2 },
    { id: 'other', label: t.other, successRate: 70, timeMultiplier: 1.4 }
  ];

  const timePeriods = [
    { id: 'week', label: t.lessThanWeek, modifier: 1.15 },
    { id: 'month', label: t.upToMonth, modifier: 1.1 },
    { id: 'quarter', label: t.oneToThreeMonths, modifier: 1.05 },
    { id: 'halfyear', label: t.threeToSixMonths, modifier: 1 },
    { id: 'year', label: t.sixToTwelveMonths, modifier: 0.9 },
    { id: 'more', label: t.moreThanYear, modifier: 0.75 }
  ];

  const calculateResult = () => {
    if (!amount || !fraudType || !timeElapsed || !hasDocuments) return;

    setIsCalculating(true);

    setTimeout(() => {
      const numAmount = parseFloat(amount.replace(/[^\d.]/g, ''));
      const fraud = fraudTypes.find(f => f.id === fraudType);
      const time = timePeriods.find(tp => tp.id === timeElapsed);
      const docModifier = hasDocuments === 'yes' ? 1.1 : 0.85;

      let successRate = fraud.successRate * time.modifier * docModifier;
      successRate = Math.min(Math.max(successRate, 35), 95);

      const estimatedReturn = numAmount * (successRate / 100);
      const minReturn = estimatedReturn * 0.85;
      const maxReturn = estimatedReturn * 1.1;

      // Calculate time in weeks (worst case = 3 weeks max)
      // Best case: 1-2 weeks, Worst case: 2-3 weeks
      let minWeeks, maxWeeks;
      
      // Good conditions: quick time + has documents
      const isGoodCase = (time.modifier >= 1.05) && (hasDocuments === 'yes');
      // Bad conditions: old case + no documents + complex fraud
      const isBadCase = (time.modifier <= 0.9) || (hasDocuments === 'no' && fraud.timeMultiplier > 1.2);
      
      if (isGoodCase) {
        minWeeks = 1;
        maxWeeks = 2;
      } else if (isBadCase) {
        minWeeks = 2;
        maxWeeks = 3;
      } else {
        minWeeks = 1;
        maxWeeks = 3;
      }

      setResult({
        successRate: Math.round(successRate),
        estimatedReturn: Math.round(estimatedReturn),
        minReturn: Math.round(minReturn),
        maxReturn: Math.round(Math.min(maxReturn, numAmount)),
        estimatedWeeks: minWeeks,
        maxWeeks: maxWeeks,
        originalAmount: numAmount
      });

      setIsCalculating(false);
    }, 1500);
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(num);
  };

  const goToForm = () => {
    navigate('/form?source=pixel2');
  };

  const isFormComplete = amount && fraudType && timeElapsed && hasDocuments;

  return (
    <section id="calculator" className="calculator-section">
      <div className="section-container">
        <div className="section-header centered">
          <div className="calculator-badge">
            <CalcIcon size={20} />
            <span>{t.calculatorBadge}</span>
          </div>
          <h2 className="section-title">{t.calculatorTitle}</h2>
          <p className="section-subtitle">{t.calculatorSubtitle}</p>
        </div>

        <div className="calculator-wrapper">
          <div className="calculator-form">
            <div className="calc-field">
              <label className="calc-label">
                <DollarSign size={18} />
                {t.lostAmount}
              </label>
              <div className="calc-input-wrapper">
                <span className="calc-currency">$</span>
                <input
                  type="text"
                  value={amount}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^\d]/g, '');
                    setAmount(val ? parseInt(val).toLocaleString() : '');
                  }}
                  placeholder="10,000"
                  className="calc-input"
                  data-testid="calculator-amount-input"
                />
              </div>
            </div>

            <div className="calc-field">
              <label className="calc-label">
                <AlertCircle size={18} />
                {t.fraudType}
              </label>
              <div className="calc-options">
                {fraudTypes.map((type) => (
                  <button
                    key={type.id}
                    className={`calc-option ${fraudType === type.id ? 'active' : ''}`}
                    onClick={() => setFraudType(type.id)}
                    data-testid={`fraud-type-${type.id}`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="calc-field">
              <label className="calc-label">
                <Clock size={18} />
                {t.whenHappened}
              </label>
              <div className="calc-options">
                {timePeriods.map((period) => (
                  <button
                    key={period.id}
                    className={`calc-option ${timeElapsed === period.id ? 'active' : ''}`}
                    onClick={() => setTimeElapsed(period.id)}
                    data-testid={`time-period-${period.id}`}
                  >
                    {period.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="calc-field">
              <label className="calc-label">
                <CheckCircle size={18} />
                {t.hasDocuments}
              </label>
              <div className="calc-options-binary">
                <button
                  className={`calc-option-binary ${hasDocuments === 'yes' ? 'active' : ''}`}
                  onClick={() => setHasDocuments('yes')}
                  data-testid="has-documents-yes"
                >
                  <CheckCircle size={20} />
                  {t.yesHave}
                </button>
                <button
                  className={`calc-option-binary ${hasDocuments === 'no' ? 'active' : ''}`}
                  onClick={() => setHasDocuments('no')}
                  data-testid="has-documents-no"
                >
                  <AlertCircle size={20} />
                  {t.noPartial}
                </button>
              </div>
            </div>

            <button
              className={`calc-submit ${!isFormComplete ? 'disabled' : ''}`}
              onClick={calculateResult}
              disabled={!isFormComplete || isCalculating}
              data-testid="calculate-button"
            >
              {isCalculating ? (
                <>
                  <span className="calc-spinner"></span>
                  {t.calculating}
                </>
              ) : (
                <>
                  <CalcIcon size={20} />
                  {t.calculate}
                </>
              )}
            </button>
          </div>

          {result && (
            <div className="calculator-result" data-testid="calculator-result">
              <div className="result-header">
                <h3>{t.resultsTitle}</h3>
                <span className="result-badge">{t.preliminaryCalc}</span>
              </div>

              <div className="result-main">
                <div className="result-success-rate">
                  <div className="rate-circle">
                    <svg viewBox="0 0 36 36" className="rate-svg">
                      <path
                        className="rate-bg"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <path
                        className="rate-progress"
                        strokeDasharray={`${result.successRate}, 100`}
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                    </svg>
                    <span className="rate-value">{result.successRate}%</span>
                  </div>
                  <span className="rate-label">{t.successProbability}</span>
                </div>

                <div className="result-amount">
                  <TrendingUp size={24} className="result-icon" />
                  <div className="amount-value">{formatCurrency(result.estimatedReturn)}</div>
                  <div className="amount-range">
                    {formatCurrency(result.minReturn)} - {formatCurrency(result.maxReturn)}
                  </div>
                  <span className="amount-label">{t.expectedReturn}</span>
                </div>

                <div className="result-time">
                  <Clock size={24} className="result-icon" />
                  <div className="time-value">{result.estimatedWeeks}-{result.maxWeeks}</div>
                  <span className="time-unit">{t.weeks}</span>
                  <span className="time-label">{t.returnPeriod}</span>
                </div>
              </div>

              <div className="result-note">
                <AlertCircle size={16} />
                <p>{t.exactConditions}</p>
              </div>

              <button className="result-cta" onClick={goToForm} data-testid="get-consultation-btn">
                {t.getFreeConsultation}
                <ArrowRight size={20} />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Calculator;
